import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Stack,
  alpha,
  useTheme,
  IconButton,
  Tooltip,
  Alert,
  Button,
  Divider,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  ErrorOutline as ErrorIcon,
  WarningAmber as WarningIcon,
  InfoOutlined as InfoIcon,
  PlayArrow as RunIcon,
} from '@mui/icons-material';
import healthService, {
  HealthChecks,
  HealthSeverity,
} from '../../../services/healthService';
import migrateService, { BackfillResult, FixIndexResult } from '../../../services/migrateService';

// ── Helper text per check ─────────────────────────────────────────────────────

const CHECK_HELPER: Record<keyof HealthChecks, string> = {
  orphanedBalances:   'Balance records pointing at a missing or inactive inventory item.',
  staleSupplierLinks: 'Active supplier links pointing at an inactive or missing inventory item.',
  stockNoSupplier:    'Items currently holding stock with no active supplier link.',
  missingBalances:    'Active inventory items with no balance record at all.',
  unusedItems:        'Active items older than 30 days with zero stock and no movements.',
  emptyWineLists:     'Wine lists with status ACTIVE but no active entries.',
  unlinkedWineItems:  'Active WINE items with no link to the wine catalog — usually left behind when the wine they were created against was rejected.',
};

// ── Severity config ───────────────────────────────────────────────────────────

const SEV_COLOR: Record<HealthSeverity, string> = {
  error:   '#ef4444',
  warning: '#f59e0b',
  info:    '#6b7280',
};

const SEV_BG: Record<HealthSeverity, string> = {
  error:   'rgba(239,68,68,0.06)',
  warning: 'rgba(245,158,11,0.06)',
  info:    'rgba(107,114,128,0.05)',
};

const SEV_ICON: Record<HealthSeverity, React.ReactNode> = {
  error:   <ErrorIcon   fontSize="small" />,
  warning: <WarningIcon fontSize="small" />,
  info:    <InfoIcon    fontSize="small" />,
};

const SEV_LABEL: Record<HealthSeverity, string> = {
  error:   'Error',
  warning: 'Warning',
  info:    'Info',
};

// ── Single health check card ──────────────────────────────────────────────────

interface CheckCardProps {
  id:     keyof HealthChecks;
  label:  string;
  count:  number;
  sev:    HealthSeverity;
  /** Present only for checks that have a list to open. */
  onOpen?: () => void;
}

const CheckCard: React.FC<CheckCardProps> = ({ id, label, count, sev, onOpen }) => {
  const theme  = useTheme();
  const color  = SEV_COLOR[sev];
  const bg     = SEV_BG[sev];
  const isZero = count === 0;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 2,
        border: `1px solid ${isZero
          ? alpha(theme.palette.divider, 0.15)
          : alpha(color, 0.3)}`,
        backgroundColor: isZero ? 'transparent' : bg,
        transition: 'border-color 0.15s',
      }}
    >
      {/* Severity indicator + label row */}
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1.5 }}>
        <Box sx={{ color: isZero ? theme.palette.text.disabled : color, display: 'flex' }}>
          {SEV_ICON[sev]}
        </Box>
        <Typography
          variant="caption"
          sx={{
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            color: isZero ? theme.palette.text.disabled : color,
          }}
        >
          {SEV_LABEL[sev]}
        </Typography>
      </Stack>

      {/* Count */}
      <Typography
        variant="h3"
        sx={{
          fontWeight: 700,
          lineHeight: 1,
          mb: 0.75,
          color: isZero ? theme.palette.text.disabled : (sev === 'error' ? color : theme.palette.text.primary),
        }}
      >
        {count}
      </Typography>

      {/* Label */}
      <Typography
        variant="body2"
        fontWeight={600}
        sx={{ mb: 0.5, color: isZero ? theme.palette.text.disabled : theme.palette.text.primary }}
      >
        {label}
      </Typography>

      {/* Helper text */}
      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
        {CHECK_HELPER[id]}
      </Typography>

      {/* SIMPLE-WINE.4 §6 — offered only when there is something to look at.
          A "view" link on a zero count is an invitation to an empty page. */}
      {onOpen && count > 0 && (
        <Button
          size="small"
          onClick={onOpen}
          sx={{ mt: 1, px: 0, textTransform: 'none', fontWeight: 600 }}
        >
          View the list
        </Button>
      )}
    </Paper>
  );
};

// ── Ordered check list ────────────────────────────────────────────────────────
// Fixed display order: errors first, then warnings, then info.

const CHECK_ORDER: Array<keyof HealthChecks> = [
  'orphanedBalances',
  'staleSupplierLinks',
  'stockNoSupplier',
  'missingBalances',
  'unusedItems',
  'emptyWineLists',
  'unlinkedWineItems',
];

// ── Main page ─────────────────────────────────────────────────────────────────

const DataHealth: React.FC = () => {
  const [loading, setLoading]           = useState(true);
  const [checks, setChecks]             = useState<HealthChecks | null>(null);
  const [generatedAt, setGeneratedAt]   = useState<string | null>(null);
  const [error, setError]               = useState<string | null>(null);

  const [backfilling, setBackfilling]       = useState(false);
  const [backfillResult, setBackfillResult] = useState<BackfillResult | null>(null);
  const [backfillError, setBackfillError]   = useState<string | null>(null);

  const [fixingIndex, setFixingIndex]           = useState(false);
  const [fixIndexResult, setFixIndexResult]     = useState<FixIndexResult | null>(null);
  const [fixIndexError, setFixIndexError]       = useState<string | null>(null);

  const [fixingWliIndex, setFixingWliIndex]     = useState(false);
  const [fixWliIndexResult, setFixWliIndexResult] = useState<FixIndexResult | null>(null);
  const [fixWliIndexError, setFixWliIndexError] = useState<string | null>(null);

  const handleBackfill = async () => {
    setBackfilling(true);
    setBackfillResult(null);
    setBackfillError(null);
    try {
      const result = await migrateService.backfillNeedsReview();
      setBackfillResult(result);
    } catch (err) {
      setBackfillError('Backfill failed. Please try again.');
      console.error('Backfill failed:', err);
    } finally {
      setBackfilling(false);
    }
  };

  const handleFixIndex = async () => {
    setFixingIndex(true);
    setFixIndexResult(null);
    setFixIndexError(null);
    try {
      const result = await migrateService.fixInventoryItemIndex();
      setFixIndexResult(result);
    } catch (err) {
      setFixIndexError('Index fix failed. Please try again.');
      console.error('Fix index failed:', err);
    } finally {
      setFixingIndex(false);
    }
  };

  const handleFixWliIndex = async () => {
    setFixingWliIndex(true);
    setFixWliIndexResult(null);
    setFixWliIndexError(null);
    try {
      const result = await migrateService.fixWineListItemIndex();
      setFixWliIndexResult(result);
    } catch (err) {
      setFixWliIndexError('Index fix failed. Please try again.');
      console.error('Fix WLI index failed:', err);
    } finally {
      setFixingWliIndex(false);
    }
  };

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await healthService.getSummary();
      setChecks(data.checks);
      setGeneratedAt(data.generatedAt);
    } catch (err) {
      setError('Failed to load health summary. Please try again.');
      console.error('Health summary fetch failed:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const navigate = useNavigate();

  // Total issues across all checks for the header callout
  const totalIssues = checks
    ? CHECK_ORDER.reduce((sum, key) => sum + (checks[key]?.count ?? 0), 0)
    : null;

  return (
    <Box sx={{ p: 3 }}>
      {/* ── Header ── */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} gutterBottom>Data Health</Typography>
          <Typography variant="body2" color="text.secondary">
            Structural integrity checks across inventory, supplier links, and wine lists.
            Computed on demand — no background jobs.
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={fetchSummary} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* ── States ── */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : checks ? (
        <>
          {/* Summary callout */}
          {totalIssues !== null && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color={totalIssues === 0 ? 'success.main' : 'text.secondary'}>
                {totalIssues === 0
                  ? 'All checks passed — no issues detected.'
                  : `${totalIssues} issue${totalIssues !== 1 ? 's' : ''} detected across all checks.`}
              </Typography>
            </Box>
          )}

          {/* Cards grid */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: 2,
            }}
          >
            {CHECK_ORDER.map(key => (
              checks[key] ? (
                <CheckCard
                  key={key}
                  id={key}
                  label={checks[key]!.label}
                  count={checks[key]!.count}
                  sev={checks[key]!.severity}
                  // SIMPLE-WINE.4 §6 — the only check with somewhere to go.
                  // A count nobody can act on is a number; a count that opens
                  // the list is a report.
                  onOpen={key === 'unlinkedWineItems'
                    ? () => navigate('/admin/governance/unlinked-wine-items')
                    : undefined}
                />
              ) : null
            ))}
          </Box>

          {/* Timestamp */}
          {generatedAt && (
            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 3 }}>
              Generated {new Date(generatedAt).toLocaleString()}
            </Typography>
          )}
        </>
      ) : null}

      {/* ── Maintenance Actions ── */}
      <Divider sx={{ my: 4 }} />
      <Box>
        <Typography variant="h6" fontWeight={600} gutterBottom>Maintenance</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          One-time data migrations. Safe to run multiple times — already-processed records are skipped.
        </Typography>

        <Paper
          elevation={0}
          sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider', maxWidth: 520 }}
        >
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
            <Box>
              <Typography variant="body2" fontWeight={600}>Backfill needsReview flag</Typography>
              <Typography variant="caption" color="text.secondary">
                Flags all existing wines, producers, regions, grapes, and wine names that have never
                been through the review queue. Run this once after initial data import.
              </Typography>
              {backfillResult && (
                <Alert severity="success" sx={{ mt: 1.5, py: 0.5 }}>
                  Flagged {backfillResult.flagged.total} records —
                  {' '}{backfillResult.flagged.wines} wines,
                  {' '}{backfillResult.flagged.producers} producers,
                  {' '}{backfillResult.flagged.regions} regions,
                  {' '}{backfillResult.flagged.grapes} grapes,
                  {' '}{backfillResult.flagged.wineNames} wine names.
                </Alert>
              )}
              {backfillError && (
                <Alert severity="error" sx={{ mt: 1.5, py: 0.5 }}>{backfillError}</Alert>
              )}
            </Box>
            <Button
              variant="outlined"
              size="small"
              startIcon={backfilling ? <CircularProgress size={14} /> : <RunIcon />}
              onClick={handleBackfill}
              disabled={backfilling}
              sx={{ flexShrink: 0 }}
            >
              {backfilling ? 'Running…' : 'Run'}
            </Button>
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider', maxWidth: 520, mt: 2 }}
        >
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
            <Box>
              <Typography variant="body2" fontWeight={600}>Fix inventory item index</Typography>
              <Typography variant="caption" color="text.secondary">
                Drops the legacy unique index that ignored bottle size, and recreates the correct one
                that treats each wine + venue + location + bottle size as a distinct item.
                Run this once if creating inventory items with different bottle sizes fails with a duplicate error.
              </Typography>
              {fixIndexResult && (
                <Alert severity="success" sx={{ mt: 1.5, py: 0.5 }}>
                  {fixIndexResult.message}
                </Alert>
              )}
              {fixIndexError && (
                <Alert severity="error" sx={{ mt: 1.5, py: 0.5 }}>{fixIndexError}</Alert>
              )}
            </Box>
            <Button
              variant="outlined"
              size="small"
              startIcon={fixingIndex ? <CircularProgress size={14} /> : <RunIcon />}
              onClick={handleFixIndex}
              disabled={fixingIndex}
              sx={{ flexShrink: 0 }}
            >
              {fixingIndex ? 'Running…' : 'Run'}
            </Button>
          </Stack>
        </Paper>
        <Paper
          elevation={0}
          sx={{ p: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider', maxWidth: 520, mt: 2 }}
        >
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
            <Box>
              <Typography variant="body2" fontWeight={600}>Fix wine list item index</Typography>
              <Typography variant="caption" color="text.secondary">
                Drops the legacy unique index that only allowed one entry per wine per list,
                and recreates the correct one that allows the same wine multiple times on the same
                list when linked to different inventory items (bottle sizes).
                Run this once if adding a wine to a wine list fails with a duplicate error.
              </Typography>
              {fixWliIndexResult && (
                <Alert severity="success" sx={{ mt: 1.5, py: 0.5 }}>
                  {fixWliIndexResult.message}
                </Alert>
              )}
              {fixWliIndexError && (
                <Alert severity="error" sx={{ mt: 1.5, py: 0.5 }}>{fixWliIndexError}</Alert>
              )}
            </Box>
            <Button
              variant="outlined"
              size="small"
              startIcon={fixingWliIndex ? <CircularProgress size={14} /> : <RunIcon />}
              onClick={handleFixWliIndex}
              disabled={fixingWliIndex}
              sx={{ flexShrink: 0 }}
            >
              {fixingWliIndex ? 'Running…' : 'Run'}
            </Button>
          </Stack>
        </Paper>
      </Box>
    </Box>
  );
};

export default DataHealth;
